/**
 * The money side of a redeployment, shown as arithmetic rather than as a verdict.
 *
 * A store EARNS its category's payout band and COSTS the monthly CTC of whoever is
 * standing in it; which end of the band it bills depends on whether anyone there meets
 * the category's tenure requirement. So every figure on screen is one sum:
 *
 *     payout − cost = margin
 *
 * The first version of this panel showed only the net change, and the feedback was
 * immediate and fair: you could not see what we pay anyone, or where the number came
 * from. Everything here is now laid out so the reader can re-do the arithmetic by eye —
 * payout, cost and margin as three columns, before and after, for BOTH stores, with each
 * store's band and tenure bar next to its name.
 *
 * ⚠️ An unknown cost is never drawn as ₹0. Someone deployed is being paid; if their CTC
 * could not be resolved, the margin is a CEILING and saying "₹61,110 profit" is worse
 * than saying nothing. `unpricedHeadcount` drives an explicit warning and suppresses the
 * confident styling — see `costIsPartial`.
 */
import type { ReactNode } from 'react';
import { AlertTriangle, IndianRupee, TrendingDown, TrendingUp } from 'lucide-react';

/** What one store earns, costs and clears in a month. */
export type StoreEconomics = {
  payout: number;
  cost: number;
  margin: number;
  /** Did anyone deployed here meet the category's tenure requirement? */
  qualifies: boolean;
  headcount: number;
  /** Deployed people whose CTC could not be resolved. > 0 ⇒ cost is understated. */
  unpricedHeadcount: number;
};

export type StoreImpact = { before: StoreEconomics; after: StoreEconomics; delta: number };

/** The band and tenure bar a store is priced under. */
export type StoreTerms = {
  categoryName: string | null;
  reqLabel: string;
  payoutMin: number | null;
  payoutMax: number | null;
};

export type RedeployFinancial = {
  mode: 'move' | 'add' | 'swap' | 'replace';
  source: StoreImpact;
  destination: StoreImpact;
  net: { before: number; after: number; delta: number };
  /** Monthly CTC of the employee being moved. 0 when unresolved. */
  employeeCost: number;
  /** Monthly CTC of the incumbent being swapped or replaced. */
  incumbentCost: number | null;
  sourceStore: StoreTerms;
  destinationStore: StoreTerms;
};

export type SourceStoreFinancials = StoreEconomics & {
  payoutMin: number | null;
  payoutMax: number | null;
  reqLabel: string;
  employeeCost: number;
};

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

/**
 * A rupee figure. Negatives carry a TYPOGRAPHIC minus (−, U+2212) rather than Intl's
 * ASCII hyphen, so a loss in the margin column and a loss in the change column — which
 * is signed by hand — are drawn with the same glyph at the same width.
 */
export const money = (value?: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const formatted = currencyFormatter.format(Math.abs(value));
  return value < 0 ? `−${formatted}` : formatted;
};

export const signedMoney = (v: number): string => `${v > 0 ? '+' : v < 0 ? '−' : ''}${money(Math.abs(v))}`;

export const deltaTone = (v: number): string =>
  v > 0 ? 'text-emerald-600' : v < 0 ? 'text-rose-600' : 'text-gray-500';

/**
 * Colour a margin ONLY when we actually know it.
 *
 * Green reads as "verified profit". On a row where somebody's CTC is missing the margin
 * is an upper bound, and painting it green is the single most misleading thing this
 * panel could do — it was showing ₹61,110 of "profit" for a store whose entire cost was
 * unknown. Unpriced rows stay neutral and let the warning carry the meaning.
 */
const marginTone = (value: number, unpricedHeadcount: number): string =>
  unpricedHeadcount > 0 ? 'text-gray-400' : deltaTone(value);

/** Is any part of this picture missing a salary? Then every margin in it is a ceiling. */
export const costIsPartial = (f: RedeployFinancial): boolean =>
  [f.source.before, f.source.after, f.destination.before, f.destination.after]
    .some((e) => e.unpricedHeadcount > 0);

const peopleLabel = (n: number): string => `${n} ${n === 1 ? 'person' : 'people'}`;

/** Warning strip — stated once, wherever a margin is shown on incomplete cost. */
const UnpricedNotice = ({ count, className = '' }: { count: number; className?: string }) => (
  <p className={`flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800 ${className}`}>
    <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
    <span>
      No CTC on record for {peopleLabel(count)} here, so the cost is understated and the margin
      below is a <span className="font-semibold">best case</span>, not a figure.
    </span>
  </p>
);

/**
 * The headline: what this move does to payout-minus-cost across BOTH stores. Net, not
 * destination-only — the store being vacated stops billing the moment it empties.
 */
export const NetImpactChip = ({ f, compact = false }: { f: RedeployFinancial; compact?: boolean }) => {
  const partial = costIsPartial(f);
  const flat = f.net.delta === 0;
  const up = f.net.delta > 0;
  const Icon = partial ? AlertTriangle : flat ? IndianRupee : up ? TrendingUp : TrendingDown;
  const tone = partial
    ? 'bg-amber-50 text-amber-700'
    : flat
      ? 'bg-gray-100 text-gray-600'
      : up
        ? 'bg-emerald-50 text-emerald-700'
        : 'bg-rose-50 text-rose-600';
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
        compact ? 'text-[11px]' : 'text-xs'
      } ${tone}`}
      title={
        partial
          ? 'Some deployed employees have no CTC on record — this figure assumes they cost nothing'
          : `Combined monthly margin of both stores: ${money(f.net.before)} → ${money(f.net.after)}`
      }
    >
      <Icon className="h-3 w-3" />
      {flat ? 'No change' : `Net ${signedMoney(f.net.delta)}/mo`}
      {partial && <span className="font-normal">· est.</span>}
    </span>
  );
};

/**
 * The money on a candidate CARD, before anyone commits to it.
 *
 * The card used to carry the net figure alone, which answers "is this a good idea?" but
 * not "why?" — and the net hides the two halves that actually differ between candidates:
 * a store can look identical on net while gaining a lot here and losing a lot there. So
 * the block states what this store earns today, then what the move does to EACH side.
 *
 * The net stays on the chip at the top of the card rather than being repeated here —
 * one signal per fact.
 */
export const CandidateFinancialBlock = ({
  f,
  sourceStoreName,
}: {
  f: RedeployFinancial;
  sourceStoreName: string;
}) => {
  const now = f.destination.before;
  const unpriced = f.destination.before.unpricedHeadcount + f.destination.after.unpricedHeadcount;
  const Cell = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="min-w-0">
      <p className="truncate text-[9px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className="truncate text-[11px] font-semibold tabular-nums">{children}</p>
    </div>
  );
  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <div className="grid grid-cols-3 gap-2">
        <Cell label="This store now">
          <span className={now.headcount === 0 ? 'text-gray-400' : marginTone(now.margin, now.unpricedHeadcount)}>
            {now.headcount === 0 ? 'vacant · ₹0' : money(now.margin)}
          </span>
          <span className="ml-1 font-normal text-gray-400">
            {now.headcount === 0 ? '' : `(${money(now.payout)} − ${money(now.cost)})`}
          </span>
        </Cell>
        <Cell label="Change here">
          <span className={marginTone(f.destination.delta, f.destination.after.unpricedHeadcount)}>
            {signedMoney(f.destination.delta)}
          </span>
        </Cell>
        <Cell label={`Change at ${sourceStoreName}`}>
          <span className={marginTone(f.source.delta, f.source.after.unpricedHeadcount)}>
            {signedMoney(f.source.delta)}
          </span>
        </Cell>
      </div>
      {unpriced > 0 && (
        <p className="mt-1 flex items-center gap-1 text-[10px] text-amber-700">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {peopleLabel(unpriced)} here with no CTC on record — these are best-case figures.
        </p>
      )}
    </div>
  );
};

/**
 * What we pay this person — shown on step 1, next to their tenure and score.
 *
 * Only their CTC. The store's own position (what it bills, what its people cost, and
 * whether that is a profit or a loss) is a STORE fact, so it is stated once at the top
 * of the step by {@link SourceStoreFinancialStrip} rather than repeated on every card.
 */
export const EmployeeCtc = ({ monthlyCtc }: { monthlyCtc: number | null }) => (
  <div className="mt-3 rounded-xl bg-gray-50 px-3 py-2">
    <p className="text-[10px] uppercase tracking-wide text-gray-400">CTC</p>
    <p className="truncate text-sm font-semibold tabular-nums text-gray-800">
      {monthlyCtc && monthlyCtc > 0 ? `${money(monthlyCtc)}/mo` : <span className="text-amber-700">not on record</span>}
    </p>
  </div>
);

const MODE_COPY: Record<RedeployFinancial['mode'], string> = {
  move: 'Moving in — the store being left stops billing.',
  add: 'Added alongside the incumbent — both salaries count against this store.',
  swap: 'Two-way swap — both stores keep billing, with the bands re-priced.',
  replace: 'Replacing the incumbent — their salary leaves the books with them.',
};

const bandReason = (f: RedeployFinancial): string => {
  const after = f.destination.after;
  if (after.payout === 0 && after.headcount > 0) return 'No payout band configured for this category';
  return after.qualifies
    ? 'Tenure requirement met — billing the top of the payout band'
    : 'Below the tenure requirement — billing the bottom of the payout band';
};

const Num = ({ value, bold = false, tone }: { value: number; bold?: boolean; tone?: string }) => (
  <td className={`px-2 py-1 text-right tabular-nums ${bold ? 'font-semibold' : ''} ${tone ?? 'text-gray-700'}`}>
    {money(value)}
  </td>
);

/**
 * One store's two rows — what it bills and costs today, and what it would after.
 * The "after" row carries the change, so the eye can read left-to-right (the sum) or
 * top-to-bottom (the movement).
 */
const StoreBlock = ({
  label,
  storeName,
  terms,
  imp,
}: {
  label: string;
  storeName: string;
  terms: StoreTerms;
  imp: StoreImpact;
}) => {
  const band =
    terms.payoutMin != null && terms.payoutMax != null && (terms.payoutMin > 0 || terms.payoutMax > 0)
      ? `${money(terms.payoutMin)}–${money(terms.payoutMax)}`
      : 'no band set';
  return (
    <>
      <tr className="border-t border-gray-100">
        <td colSpan={5} className="pb-1 pt-2.5">
          <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
          <p className="font-semibold text-gray-900">{storeName}</p>
          <p className="text-[11px] text-gray-400">
            {[terms.categoryName, terms.reqLabel ? `requires ${terms.reqLabel}` : 'no tenure requirement', `band ${band}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </td>
      </tr>
      <tr>
        <td className="py-1 pl-3 pr-2 text-gray-500">
          now
          <span className="ml-1 text-[11px] text-gray-400">
            ({imp.before.headcount === 0 ? 'vacant' : peopleLabel(imp.before.headcount)})
          </span>
        </td>
        <Num value={imp.before.payout} />
        <Num value={imp.before.cost} tone={imp.before.unpricedHeadcount > 0 ? 'text-amber-700' : undefined} />
        <Num value={imp.before.margin} tone={marginTone(imp.before.margin, imp.before.unpricedHeadcount)} />
        <td />
      </tr>
      <tr>
        <td className="py-1 pl-3 pr-2 text-gray-500">
          after
          <span className="ml-1 text-[11px] text-gray-400">
            ({imp.after.headcount === 0 ? 'vacant' : peopleLabel(imp.after.headcount)})
          </span>
        </td>
        <Num value={imp.after.payout} bold />
        <Num value={imp.after.cost} bold tone={imp.after.unpricedHeadcount > 0 ? 'text-amber-700' : undefined} />
        <Num value={imp.after.margin} bold tone={marginTone(imp.after.margin, imp.after.unpricedHeadcount)} />
        <td
          className={`py-1 pl-2 text-right font-semibold tabular-nums ${marginTone(
            imp.delta,
            imp.before.unpricedHeadcount + imp.after.unpricedHeadcount,
          )}`}
        >
          {signedMoney(imp.delta)}
        </td>
      </tr>
    </>
  );
};

/** Who physically moves, and what we pay them — the two facts the table cannot show. */
const MoveLine = ({
  f,
  employeeName,
  incumbentName,
  fromName,
  toName,
}: {
  f: RedeployFinancial;
  employeeName: string;
  incumbentName?: string | null;
  fromName: string;
  toName: string;
}) => {
  // The "CTC" label is printed by the caller, so the missing-value text completes that
  // sentence rather than repeating the word.
  const ctc = (v: number | null | undefined) =>
    v && v > 0 ? `${money(v)}/mo` : <span className="text-amber-700">not on record</span>;
  return (
    <div className="space-y-1 rounded-xl bg-gray-50 px-3 py-2 text-xs">
      <p className="text-gray-700">
        <span className="font-semibold text-gray-900">{employeeName}</span>{' '}
        <span className="text-gray-400">· CTC</span> <span className="font-semibold">{ctc(f.employeeCost)}</span>
        <span className="text-gray-400"> · {fromName} → {toName}</span>
      </p>
      {incumbentName && f.mode !== 'add' && (
        <p className="text-gray-700">
          <span className="font-semibold text-gray-900">{incumbentName}</span>{' '}
          <span className="text-gray-400">· CTC</span> <span className="font-semibold">{ctc(f.incumbentCost)}</span>
          <span className="text-gray-400">
            {f.mode === 'swap' ? ` · ${toName} → ${fromName}` : ' · leaves the payroll (NSW)'}
          </span>
        </p>
      )}
    </div>
  );
};

/** The full before → after picture, shown on the Review step before confirming. */
export const FinancialImpactPanel = ({
  f,
  fromName,
  toName,
  employeeName,
  incumbentName,
}: {
  f: RedeployFinancial;
  fromName: string;
  toName: string;
  employeeName: string;
  incumbentName?: string | null;
}) => {
  const unpriced = Math.max(
    f.source.before.unpricedHeadcount + f.destination.before.unpricedHeadcount,
    f.source.after.unpricedHeadcount + f.destination.after.unpricedHeadcount,
  );
  return (
    <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Financial impact</p>
          <p className="mt-0.5 text-xs text-gray-500">{MODE_COPY[f.mode]}</p>
        </div>
        <NetImpactChip f={f} />
      </div>

      <MoveLine f={f} employeeName={employeeName} incumbentName={incumbentName} fromName={fromName} toName={toName} />

      {unpriced > 0 && <UnpricedNotice count={unpriced} />}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[460px] text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-gray-400">
              <th className="pb-1 text-left font-medium">Store</th>
              <th className="px-2 pb-1 text-right font-medium">Payout</th>
              <th className="px-2 pb-1 text-right font-medium">Cost (CTC)</th>
              <th className="px-2 pb-1 text-right font-medium">Margin</th>
              <th className="pb-1 pl-2 text-right font-medium">Change</th>
            </tr>
          </thead>
          <tbody>
            <StoreBlock label="From" storeName={fromName} terms={f.sourceStore} imp={f.source} />
            <StoreBlock label="To" storeName={toName} terms={f.destinationStore} imp={f.destination} />
            <tr className="border-t-2 border-gray-200">
              <td className="py-1.5 pr-2 font-semibold text-gray-900">Net · both stores</td>
              <td />
              <td />
              <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-gray-900">
                {money(f.net.before)} → {money(f.net.after)}
              </td>
              <td
                className={`py-1.5 pl-2 text-right font-bold tabular-nums ${
                  costIsPartial(f) ? 'text-gray-400' : deltaTone(f.net.delta)
                }`}
              >
                {signedMoney(f.net.delta)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-gray-500">{bandReason(f)}.</p>
    </div>
  );
};

/**
 * Where the money stands BEFORE anything moves — the baseline every candidate's net
 * figure is measured against. Spelled as the sum, not as three loose numbers.
 */
export const SourceStoreFinancialStrip = ({
  f,
  storeName,
}: {
  f: SourceStoreFinancials;
  storeName: string;
}) => (
  <div className="space-y-1.5 rounded-xl border border-gray-200 bg-gray-50/70 px-3.5 py-2.5">
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
      <p className="text-xs font-semibold text-gray-700">
        {storeName} today
        {f.payoutMin != null && f.payoutMax != null && (
          <span className="ml-1.5 font-normal text-gray-400">
            band {money(f.payoutMin)}–{money(f.payoutMax)}
            {f.reqLabel ? ` · requires ${f.reqLabel}` : ''}
          </span>
        )}
      </p>
      <p className="flex flex-wrap items-center gap-1.5 text-xs tabular-nums text-gray-500">
        <span>
          Payout <span className="font-semibold text-gray-700">{money(f.payout)}</span>
        </span>
        <span className="text-gray-300">−</span>
        <span>
          Cost{' '}
          <span className={`font-semibold ${f.unpricedHeadcount > 0 ? 'text-amber-700' : 'text-gray-700'}`}>
            {money(f.cost)}
          </span>
          <span className="ml-0.5 text-[11px] text-gray-400">
            ({f.headcount === 0 ? 'vacant' : peopleLabel(f.headcount)})
          </span>
        </span>
        <span className="text-gray-300">=</span>
        <span>
          Margin{' '}
          <span className={`font-semibold ${marginTone(f.margin, f.unpricedHeadcount)}`}>{money(f.margin)}</span>
          {f.unpricedHeadcount === 0 && f.headcount > 0 && (
            <span className={`ml-1 text-[11px] font-semibold ${f.margin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {f.margin >= 0 ? '· in profit' : '· at a loss'}
            </span>
          )}
        </span>
      </p>
    </div>
    {f.unpricedHeadcount > 0 && <UnpricedNotice count={f.unpricedHeadcount} />}
  </div>
);
